import ChevronDownThick from '@/icons/chevron-down-thick';
import ChevronUpThick from '@/icons/chevron-up-thick';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shadcn-ui/accordion';
import { useState } from 'react';

interface SavingsCalculatorCollapsibleSectionProps {
  title: string;
  titleIcon: React.ReactNode;
  children: React.ReactNode;
  /** Component to display below title when accordion is currently condensed */
  subTrigger?: React.ReactNode;
  /** Controls whether the subTrigger should continue to be displayed or not when the accordion main content is expanded */
  keepSubTriggerVisible?: boolean;
  /** Overrides for the far right icon when the accordion expands and collapses */
  iconOverride?: {
    openedIcon?: React.ReactNode;
    closedIcon?: React.ReactNode;
  };
}

export default function SavingsCalculatorCollapsibleSection({
  title,
  titleIcon,
  children,
  subTrigger,
  keepSubTriggerVisible,
  iconOverride,
}: SavingsCalculatorCollapsibleSectionProps) {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => setExpanded((prev) => !prev);

  // We need to control what icon shows for the trigger because we're using one accordion item to control the
  // expanded and condensed states of the other accordion items.
  const titleTriggerIcon = expanded
    ? (iconOverride?.openedIcon ?? <ChevronUpThick className="flex-shrink-0" />)
    : (iconOverride?.closedIcon ?? <ChevronDownThick className="flex-shrink-0" />);

  return (
    <Accordion type="single" collapsible value={expanded ? 'expanded' : 'condensed'}>
      <AccordionItem value="trigger" className="border-none p-0">
        <AccordionTrigger
          className="flex flex-row justify-between gap-2 pb-[16px] pt-0 hover:no-underline"
          hideIcon
          onClick={handleToggle}
        >
          <div className="flex h-[24px] w-full items-center justify-between">
            <div className="flex gap-[8px]">
              {titleIcon}
              <h3>{title}</h3>
            </div>

            {titleTriggerIcon}
          </div>
        </AccordionTrigger>
      </AccordionItem>

      {/* NOTE: the order of the below accordion items are important in order to get nice animations when these accordion items open and close simultaneously. */}

      {/* When subTrigger is permanent, we need to use AccordionTrigger so that it stays after the main content is expanded */}
      {keepSubTriggerVisible ? (
        <AccordionItem value="permanent-sub-trigger" className="border-none">
          <AccordionTrigger
            className="p-0 hover:no-underline"
            hideIcon
            onClick={handleToggle}
          >
            {subTrigger}
          </AccordionTrigger>
        </AccordionItem>
      ) : null}

      <AccordionItem value="expanded" className="border-none">
        <AccordionContent className="p-0">{children}</AccordionContent>
      </AccordionItem>

      {/* When subTrigger is not always visible, we need to use AccordionContent so that it properly collapses when the main content is expanded */}
      {!keepSubTriggerVisible ? (
        <AccordionItem value="condensed" className="border-none">
          <AccordionContent className="p-0">
            <div
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={handleToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleToggle();
                }
              }}
            >
              {subTrigger}
            </div>
          </AccordionContent>
        </AccordionItem>
      ) : null}
    </Accordion>
  );
}
